import React from 'react';
import { useParams } from 'react-router-dom';
import { Map } from 'immutable';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { createFavorite, deleteFavorite } from 'flow/actions/favorites-actions';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useAppContext from 'flow/hooks/useAppContext';
import selectFavoriteData, {
    selectFavoriteFromEntityKey,
} from 'flow/selectors/favorite-selectors';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { FavoriteEntityKey } from 'flow/types/Favorite';

const FavoriteListItem = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    const { entityKey } = useParams<{
        entityKey: FavoriteEntityKey;
    }>();
    const favorites = useAppSelector((state) => state.favorites);
    const favoriteData = useAppSelector((state) =>
        selectFavoriteData(entityKey, state),
    );

    const favorite = selectFavoriteFromEntityKey(entityKey, favorites);

    // Derived from the store, never copied into local state. As a useState seed
    // it answered to clicks rather than to the stored favourites, so a refused
    // save, a refetch, or a toggle from one of the context menus left it stale —
    // and this row now feeds it to aria-checked, which would state a favourite
    // that is not there. Deriving it also means checked can never be true while
    // `favorite` is undefined, which is what made the delete branch throw.
    const checked = !!favorite;

    const toggle = () => {
        if (!checked) {
            const [favoriteType] = entityKey.split('-');
            if (
                ['extension', 'contact', 'queue', 'refer'].includes(
                    favoriteType,
                )
            ) {
                dispatch(
                    createFavorite(
                        Map({
                            type: favoriteType,
                            [favoriteType]: favoriteData,
                        }),
                    ),
                );
            }
        } else {
            dispatch(deleteFavorite(favorite.get('key')));
        }
    };

    // The hover hint names the action, so it flips with state. It cannot become
    // the switch's name: Tooltip exposes its label as a description on its own
    // wrapper, never as the name of what it wraps.
    const hint = i18next.t(
        checked
            ? 'General.Favorite.Remove.Favorite'
            : 'General.Favorite.Add.Favorite',
    );

    return (
        // A real button, not a clickable div: the whole row was the click
        // target but carried no role, so it announced as inert text and could
        // not be activated with a keyboard at all. role="switch" + aria-checked
        // is what conveys the on/off state, since the only other cue is which
        // star icon is drawn. The name comes from the visible "Favorite" text
        // rather than an aria-label, so it stays invariant as the state flips
        // and still matches what a voice-control user sees on screen.
        // Tooltip goes *outside* the button, the way IconButton uses it. Putting
        // it inside would nest its tabIndex={0} trigger within the button.
        // w-full on the wrapper so the row still spans the settings list, and
        // bottom-end so the hint sits under the star rather than centred under
        // the whole row. `end` is logical, so it follows the star in RTL too.
        <Tooltip label={hint} placement="bottom-end" tooltipClassName="w-full">
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                onClick={toggle}
                className="flex w-full cursor-pointer flex-wrap items-start justify-between px-2 py-4 text-start hover:bg-gray-100"
            >
                <div className="text-base/5 text-gray-800">
                    {i18next.t('General.Favorite.Toggle.Title')}
                </div>

                {checked ? (
                    <Star className="text-yellow" aria-hidden={true} />
                ) : (
                    <StarBorder className="text-gray-500" aria-hidden={true} />
                )}
            </button>
        </Tooltip>
    );
};

export default FavoriteListItem;
